import type { ProethicaTestKind } from "@constants/demo/proethica-tests";
import { diagnosticItems } from "@constants/mocked-data";
import { events } from "@constants/tracking";
import { RecommendationsSkeleton } from "@features/dashboard/components/Skeletons/RecommendationCardSkeleton";
import i18n from "@helpers/lib/i18n";
import { diagnosticTagIds } from "@helpers/session";
import { smartPluralize } from "@helpers/smartPluralize";
import useAnalytics from "@hooks/useAnalytics";
import { useAppTheme } from "@hooks/useAppTheme";
import { useDemoExamStore } from "@hooks/useDemoExamStore";
import type { PracticeTestExam } from "@memorang/types";
import {
	Box,
	type IconType,
	LoaderDialog,
	MicrophonePermissionDialog,
	useDevice,
} from "@memorang/ui";
import RecommendationSessionCard from "components/RecommendationSessionCard";
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import { Text } from "react-native-paper";
import { Apps } from "types/apps";
import type { DiagnosticCardProps } from "types/tag";
import useDiagnostic from "./useDiagnostic";

type Props = {
	exams?: PracticeTestExam[];
	hasAccess?: boolean;
	handleShowPurchase: () => void;
	paddingHorizontal?: number;
	handleClose?: () => void;
	isSessionConfig?: boolean;
	isDataLoading?: boolean;
	disableAll?: boolean;
	recommendationItems?: DiagnosticItem[];
};

type DiagnosticItem = DiagnosticCardProps & {
	id?: string | number;
	disabled?: boolean;
	hasAccess?: boolean;
	testKind?: ProethicaTestKind;
};
const DiagnosticItemsContainer = ({
	exams = diagnosticItems as unknown as PracticeTestExam[],
	handleShowPurchase,
	paddingHorizontal,
	handleClose,
	isSessionConfig,
	disableAll,
	isDataLoading,
	recommendationItems,
}: Props) => {
	const { t } = useTranslation();
	const demoExamId = useDemoExamStore((state) => state.currentExam.id);
	const isProethica = demoExamId === Apps.Proethica;
	const sectionTitleKey = isProethica
		? "dashboard.recommendations.title"
		: "dashboard.diagnostics.title";

	const { isMobile } = useDevice();

	const theme = useAppTheme();
	const { explicitCallTrackCustomEvent } = useAnalytics();

	const {
		handleCreateSession,
		handleViewReport,
		showPermissionDialog,
		isCreatingSession,
		selectedItemId,
		setShowPermissionDialog,
		messages,
	} = useDiagnostic();

	const handleClickCard = (id: number, sessionNumericId?: number) => {
		if (sessionNumericId) {
			handleViewReport(sessionNumericId);
		} else {
			handleCreateSession({
				id,
				exams,
				handleClose,
			});
		}
	};

	const mappedDiagnosticItems: DiagnosticItem[] | undefined =
		recommendationItems ??
		exams?.map((item) => {
			const numItems = item.numItems;
			const inProgressCount =
				"numPointsEarned" in item && typeof item.numPointsEarned === "number"
					? item.numPointsEarned
					: item.numCompleted;
			const numCompleted =
				item.status === "COMPLETE"
					? item.numItems
					: item.status === "ACTIVE"
						? inProgressCount
						: 0;
			const timePerItem = isProethica
				? 2.5
				: Number(item.id) === diagnosticTagIds.readingListening
					? 2.5
					: 5;
			const timeToComplete = Math.round(item.numItems * timePerItem);
			const isCompleted = item.status === "COMPLETE";
			const isInProgress = item.status === "ACTIVE";
			const completedCtaKey = isProethica
				? "dashboard.diagnostics.viewResults"
				: "dashboard.diagnostics.viewReport";

			return {
				title: i18n.exists(`dashboard.diagnostics.${item.title}`)
					? t(`dashboard.diagnostics.${item.title}`)
					: item.title,
				icon: item.icon as IconType,
				id: item.id,
				loading: false,
				handleClickCard: () => {
					explicitCallTrackCustomEvent({
						eventName: events.sessionDiagnosticClicked,
						examId: item.id,
						status: item.status,
					});
					handleClickCard(Number(item.id), item.numericId);
				},
				numCompleted,
				numItems,
				button: {
					cta: t(
						isCompleted
							? completedCtaKey
							: isInProgress
								? "dashboard.diagnostics.resume"
								: "dashboard.diagnostics.start",
					),
					icon: isCompleted
						? "chart-bar"
						: isInProgress
							? "play-pause"
							: "play-outline",
					handleClick: () => {
						handleClickCard(Number(item.id), item.numericId);
					},
				} as const,
				subtitle: isCompleted
					? t("dashboard.diagnostics.completed", {
							count: numCompleted,
							item: smartPluralize("item", numCompleted),
						})
					: isInProgress
						? t("dashboard.diagnostics.inProgress", {
								completed: numCompleted,
								count: numItems,
								minutes: Math.round((numItems - numCompleted) * timePerItem),
								item: smartPluralize("item", numItems),
							})
						: t("dashboard.diagnostics.duration", {
								count: numItems,
								minutes: timeToComplete,
								item: smartPluralize("item", numItems),
							}),
			};
		});

	const isSingleModuleRecommendation =
		isProethica &&
		(mappedDiagnosticItems?.filter(
			(item) => Boolean(item.thumbnailSource) || Boolean(item.testKind),
		).length ?? 0) === 1;

	return (
		<Box gap={12}>
			<Box
				justifyContent="space-between"
				alignItems="center"
				flexDirection="row"
				paddingHorizontal={(paddingHorizontal ?? isMobile) ? 16 : 0}
			>
				<Text
					style={{
						color: theme.colors.onSurface,
						fontWeight: "bold",
					}}
					variant="titleMedium"
				>
					{t(sectionTitleKey)}
				</Text>
			</Box>
			{isDataLoading ? (
				<RecommendationsSkeleton />
			) : (
				<ScrollView
					horizontal
					contentContainerStyle={{
						gap: 8,
						paddingHorizontal: (paddingHorizontal ?? isMobile) ? 16 : 0,
						width: "100%",
						paddingBottom: 1,
						paddingLeft: isMobile ? 16 : isSessionConfig ? 16 : 1,
					}}
					showsHorizontalScrollIndicator={false}
				>
					{mappedDiagnosticItems?.map((item) => {
						const loading =
							isCreatingSession &&
							selectedItemId === Number(item.id ?? 0) &&
							isSessionConfig;
						const isModuleRecommendation =
							isProethica &&
							(Boolean(item.thumbnailSource) || Boolean(item.testKind));
						const constrainSoleCard =
							isModuleRecommendation &&
							isSingleModuleRecommendation &&
							!isMobile;
						return (
							<Box
								key={item.title}
								flex={constrainSoleCard ? undefined : 1}
								minWidth={isModuleRecommendation ? 0 : 180}
								maxWidth={isModuleRecommendation ? undefined : 360}
								style={
									constrainSoleCard
										? { width: "50%" }
										: isModuleRecommendation
											? { flexBasis: 0 }
											: undefined
								}
							>
								<RecommendationSessionCard
									{...item}
									loading={loading}
									isSessionConfig={isSessionConfig}
									onPurchasePress={
										item.hasAccess === false ? handleShowPurchase : undefined
									}
									disabled={
										item.disabled ||
										(isCreatingSession && isSessionConfig) ||
										disableAll ||
										isDataLoading
									}
								/>
							</Box>
						);
					})}
				</ScrollView>
			)}
			{showPermissionDialog && (
				<MicrophonePermissionDialog
					showDialog={showPermissionDialog}
					setShowDialog={() => {
						setShowPermissionDialog(false);
					}}
				/>
			)}
			{isCreatingSession && !isSessionConfig ? (
				<LoaderDialog
					visible
					messages={messages.map((message) => ({
						text: message,
					}))}
					handleClose={handleClose}
				/>
			) : null}
		</Box>
	);
};

export default DiagnosticItemsContainer;
